import { useRouter } from 'next/router'
import { useEffect, type HTMLProps } from 'react'

import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIPelaksanaanDocumentPreparation,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { formatDate } from 'utils/datetime'
import { stripHTMLTags } from 'utils/text'
import { Header } from '../components/Header'
import { IndicatorCard } from '../components/IndicatorCard'

interface StepProps extends HTMLProps<HTMLDivElement> {
  number?: number
  title?: string
  subtitle?: string
  hideStepLine?: boolean
}
const Step = ({
  number,
  title,
  hideStepLine = false,
  children,
  ...otherProps
}: StepProps) => {
  return (
    <div className="mt-4 flex gap-6" {...otherProps}>
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex items-center justify-center rounded-full bg-blue-70 p-4">
          <Text as="span" color="inverse" className="absolute">
            {number}
          </Text>
        </div>
        {!hideStepLine && <Separator orientation="vertical" />}
      </div>

      <div className="pb-8 mb-1">
        {title && <Text variant="heading-sm">{title}</Text>}

        {children}
      </div>
    </div>
  )
}

const RincianObservasi = () => {
  const router = useRouter()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const CURRENT_SCREEN_NAME = 'form_b'
  const ENTRY_POINT = 'pelaksanaan_hub'

  useEffect(() => {
    trackEvent({
      activity_type: 'pelaksanaan_open_form_b',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading: isLoadingPelaksanaanProgress } =
    useAPIPelaksanaanProgress({
      opts: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  const { data: documentData } = useAPIPelaksanaanDocumentPreparation({
    opts: {
      useErrorBoundary: true,
    },
  })

  const handleRubrikDialogOpened = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_b_cek_rubrik_observasi',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
  }

  const handleRubrikDialogClosed = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_b_mengerti_rubrik_observasi',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
  }

  return (
    <div className="pt-14 px-8 pb-8">
      <div className="mx-auto max-w-6xl">
        <Header
          title="Rincian Observasi"
          onBack={() => {
            router.back()
          }}
        />
        {/* content start from here */}
        <div className="max-w-6xl m-auto">
          <div className="mt-9 bg-blue-5 p-6 rounded-t border-subdued">
            <Text variant="heading-lg" as="h2">
              Langkah-langkah Pelaksanaan Observasi Praktik Kinerja
            </Text>
          </div>
          <div className="bg-white py-6 rounded-b-lg border-subdued">
            <div className="px-10">
              <Step number={1} title="Observasi akan dilakukan sesuai jadwal">
                <div className="space-y-1">
                  <Text color="subdued">Berikut jadwal observasi Anda:</Text>
                  <div className="flex">
                    <div className="space-y-1">
                      <Text color="subdued">Tanggal</Text>
                      <Text color="subdued">Jenjang</Text>
                      <Text color="subdued">Kelas</Text>
                      <Text color="subdued">
                        Mata Pelajaran/Modul Proyek&nbsp;
                      </Text>
                    </div>
                    <div className="space-y-1">
                      <Text color="subdued">
                        {`: ${formatDate(
                          documentData?.data?.detail?.observationDate || null
                        )}`}
                      </Text>
                      <Text color="subdued">
                        : {documentData?.data?.detail?.observationLevel}
                      </Text>
                      <Text color="subdued">
                        : {documentData?.data?.detail?.observationClass}
                      </Text>
                      <Text color="subdued">
                        : {documentData?.data?.detail?.observationSubject}
                      </Text>
                    </div>
                  </div>
                </div>
              </Step>
              <Step
                number={2}
                title="Atasan akan mengisi Dokumen Rating Observasi"
              >
                <div className="space-y-1">
                  <Text color="subdued">
                    Rating observasi dapat didiskusikan untuk Pelaksanaan Tindak
                    Lanjut nantinya.
                  </Text>
                  <IndicatorCard
                    subIndicatorName={data?.data?.subIndicatorName}
                    subIndicatorDefinition={data?.data?.subIndicatorDefinition}
                    qualityTarget={data?.data?.qualityTarget}
                    contentId={data?.data?.contentId}
                    isLoading={isLoadingPelaksanaanProgress}
                    onRubrikDialogOpened={handleRubrikDialogOpened}
                    onRubrikDialogClosed={handleRubrikDialogClosed}
                  />
                  <div className="flex !mt-9">
                    <div className="w-6/12 space-y-1">
                      <Text variant="heading-sm">
                        Target Perilaku Pilihan Guru
                      </Text>
                      <ul className="list-disc !px-4 space-y-1">
                        {documentData?.data?.detail?.behavioralTargets.map(
                          (thisBehavior, index) => (
                            <Text key={`behavior_${index}`} as="li">
                              {thisBehavior.behavioralTarget}
                            </Text>
                          )
                        )}
                      </ul>
                    </div>
                    <div className="w-6/12 space-y-1">
                      <Text variant="heading-sm">Upaya Mempelajari</Text>
                      <Text className="simple-wysiwyg-content overflow-wrap-anywhere">
                        {stripHTMLTags(
                          documentData?.data.detail.behavioralTargetEffort
                        )}
                      </Text>
                    </div>
                  </div>
                </div>
              </Step>
              <Step
                number={3}
                title="Persiapkan dengan matang dan semangat!"
                hideStepLine
              >
                <Text color="subdued">
                  Pelajari Rubrik Observasi dan informasi lainnya di dokumen
                  ini.
                </Text>
              </Step>
            </div>
            <div className="px-9">
              <Text variant="body-bold">Perlu diperhatikan bahwa:</Text>
              <Text className="mb-6">
                Rubrik Observasi hanya ditujukan untuk membantu atasan ketika
                observasi berlangsung.
              </Text>
              <Text>Semoga observasinya berjalan lancar, ya.</Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RincianObservasi
